import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import { getTranslation, SharedLanguageService } from '@language';
import { hasOwnPropertyGuard } from '../../common/helpers/objects/has-own-property-guard.helper';
import { SHORT_DATE_FORMAT } from '../entities/field-definition.constants';
import {
    BaseFlatFieldDefinition,
    BaseNestedFieldDefinition,
    DottedKey,
    FieldType
} from '../entities/field-definition.entity';

@Injectable({ providedIn: 'root' })
export class FieldDefinitionHelper {
    // #region Type checks (Single, List, Map, Complex, ComplexList)
    public static IsElementary(fieldDef: BaseNestedFieldDefinition): boolean {
        return fieldDef.Children?.length === 0;
    }

    public static IsElementaryList(fieldDef: BaseNestedFieldDefinition): boolean {
        return fieldDef.Children?.length === 1 && FieldDefinitionHelper.IsElementary(fieldDef.Children[0]);
    }

    public static IsMap(fieldDef: BaseNestedFieldDefinition): boolean {
        return (
            (fieldDef.Children &&
                fieldDef.Children.length > 1 &&
                fieldDef.Children.every(childDef => FieldDefinitionHelper.IsElementary(childDef))) ??
            false
        );
    }

    public static IsComplexList(fieldDef: BaseNestedFieldDefinition): boolean {
        return (
            fieldDef.Type === FieldType.MultiItemField ||
            (fieldDef.Children?.length === 1 &&
                (FieldDefinitionHelper.IsMap(fieldDef.Children[0]) ||
                    FieldDefinitionHelper.IsComplex(fieldDef.Children[0])))
        );
    }

    public static IsComplex(fieldDef: BaseNestedFieldDefinition): boolean {
        return (
            (FieldDefinitionHelper.IsMap(fieldDef) ||
                (fieldDef.Children &&
                    fieldDef.Children.length > 1 &&
                    fieldDef.Children.some(
                        childDef =>
                            FieldDefinitionHelper.IsMap(childDef) ||
                            FieldDefinitionHelper.IsElementaryList(childDef) ||
                            FieldDefinitionHelper.IsComplex(childDef) ||
                            FieldDefinitionHelper.IsComplexList(childDef)
                    ))) ??
            false
        );
    }

    public static IsList(fieldDef: BaseNestedFieldDefinition): boolean {
        // MED-1955: Descriptive.Address has a single defined subfield which would register as an ElementaryListField
        if (fieldDef.Type === FieldType.MapField) {
            return false;
        }
        return FieldDefinitionHelper.IsElementaryList(fieldDef) || FieldDefinitionHelper.IsComplexList(fieldDef);
    }
    // #endregion

    // #region Nesting
    public static getDottedKeyRoot(dottedKey: DottedKey): DottedKey | undefined {
        const dottedKeyParts: string[] = dottedKey.split('.');

        if (dottedKeyParts.length < 2) {
            console.error(`${dottedKey} is not a valid dottedKey`);
            return undefined;
        }

        return dottedKey.split('.').slice(0, 2).join('.') as DottedKey;
    }

    public static getNestedByDottedKey(
        root: BaseNestedFieldDefinition,
        dottedKey: DottedKey
    ): BaseNestedFieldDefinition | undefined {
        let keyIndex: number = 2;
        const dottedKeyParts: string[] = dottedKey.split('.');
        let currentDef: BaseNestedFieldDefinition = root;

        while (keyIndex < dottedKeyParts.length) {
            const currentKey: string = dottedKeyParts[keyIndex];

            const nextDef: BaseFlatFieldDefinition | undefined = currentDef.Children?.find(
                child => child.Key === currentKey
            );
            if (!nextDef) {
                console.error(`Could not find ${dottedKey} as child of ${root.DottedKey}`);
                return undefined;
            }

            currentDef = nextDef;
            keyIndex++;
        }
        return currentDef;
    }

    public static nestFlatFieldDefinitions(fieldDefs: BaseFlatFieldDefinition[]): BaseNestedFieldDefinition[] {
        const nestedFieldDefinitions: BaseNestedFieldDefinition[] = fieldDefs.map(flat =>
            FieldDefinitionHelper.nestFlatFieldDefinition(flat, fieldDefs)
        );
        return nestedFieldDefinitions;
    }

    public static nestFlatFieldDefinition(
        flat: BaseFlatFieldDefinition,
        all: BaseFlatFieldDefinition[]
    ): BaseNestedFieldDefinition {
        const children = flat.ChildrenFlat?.map(childFlat =>
            all.find(def => def.FieldDefinitionId === childFlat.FieldDefinitionId)
        )
            .filter((childDef): childDef is BaseFlatFieldDefinition => !!childDef)
            .map(childFlat => FieldDefinitionHelper.nestFlatFieldDefinition(childFlat, all));

        return {
            ...flat,
            Children: children ?? []
        };
    }
    // #endregion

    // #region Parent enhancement
    public static enhanceFlatFieldDefinitionsParents(fieldDefs: BaseFlatFieldDefinition[]): BaseFlatFieldDefinition[] {
        const idRefs: { [key: number]: BaseFlatFieldDefinition } = fieldDefs.reduce(
            (hashMap, item) => ({ ...hashMap, [item.FieldDefinitionId]: item }),
            {}
        );

        return fieldDefs.map(fieldDef => {
            if (fieldDef.ParentId) {
                fieldDef.Parent = idRefs[fieldDef.ParentId];
            }

            return fieldDef;
        });
    }
    // #endregion

    // #region Label enhancement
    public static enhanceFlatFieldDefinitionsLabels(
        fieldDefs: BaseFlatFieldDefinition[],
        translateService: SharedLanguageService
    ): BaseFlatFieldDefinition[] {
        return fieldDefs.map(fieldDef =>
            FieldDefinitionHelper.enhanceFlatFieldDefinitionLabels(fieldDef, translateService)
        );
    }

    private static enhanceFlatFieldDefinitionLabels(
        fieldDef: BaseFlatFieldDefinition,
        translateService: SharedLanguageService
    ): BaseFlatFieldDefinition {
        const Label: string = FieldDefinitionHelper.getFieldTranslation(fieldDef, 'Translations', translateService);

        // There is no exposed LongLabel property by the API, but makes things easier to work with.
        const LongLabel: string = FieldDefinitionHelper.getFieldTranslation(
            fieldDef,
            'LongTranslations',
            translateService
        );

        return {
            ...fieldDef,
            Label,
            LongLabel
        };
    }

    public static getFieldTranslation(
        fieldDef: BaseFlatFieldDefinition,
        translationField: 'Translations' | 'LongTranslations',
        translateService: SharedLanguageService
    ): string {
        const languageCode: string = translateService.getCurrentLanguage();
        const override: string | null = getTranslation(translateService, fieldDef.DottedKey);

        // Provide application override if available
        if (override) {
            return override;
        }

        // Safeguard to ensure current application language-label is used
        if (hasOwnPropertyGuard(fieldDef[translationField], languageCode)) {
            return fieldDef[translationField][languageCode];
        }

        // Use API-provided label only when current application language is not supported
        if (fieldDef.Label) {
            return fieldDef.Label;
        }

        // If no label can be found this way, use Key as fallback
        return fieldDef.Key;
    }
    // #endregion

    constructor(private readonly datePipe: DatePipe) {}

    // #region Value formating
    convertToString(fieldType: FieldType, value: any): string {
        switch (fieldType) {
            case FieldType.SimpleField:
                return this.parseString(value);
            case FieldType.LongField:
                return this.parseNumber(value);
            case FieldType.DateField:
                return this.parseDate(value);
            default:
                return this.parseString(value);
        }
    }

    private parseNumber(value: number): string {
        const defaultValue = 0;

        if (typeof value === 'number') {
            return value.toString();
        }

        return defaultValue.toString();
    }

    private parseString(value: string): string {
        const defaultValue = '';

        if (typeof value === 'string') {
            return value;
        }

        return defaultValue;
    }

    private parseDate(value: Date): string {
        const defaultValue = '';

        if (value instanceof Date) {
            return this.datePipe.transform(value, SHORT_DATE_FORMAT) || defaultValue;
        }

        return defaultValue;
    }
    // #endregion
}
